import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Divider
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { 
  checkServerHealth, 
  testGoogleSheetsAuth, 
  generateAccessPass, 
  GenerateAccessPassRequest 
} from '../services/serverService';

const GoogleSheetsIntegrationTest: React.FC = () => {
  // State for the API key input
  const [apiKey, setApiKey] = useState<string>('sk_5eb6ee5964f75c2c29e2a61000cca8a08dda51f266f36f97');
  const [apiKeyValid, setApiKeyValid] = useState<boolean | null>(null);
  
  // State for server status
  const [serverStatus, setServerStatus] = useState<'Connected' | 'Disconnected' | 'Checking'>('Checking');
  
  // State for form fields
  const [formData, setFormData] = useState<GenerateAccessPassRequest>({
    customerName: '',
    contactNumber: '',
    validDate: new Date().toISOString().split('T')[0],
    pickupTime: '10:00',
    returnTime: '18:00',
    type: 'Collection'
  });
  
  // State for validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // State for submission
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Check server status on component mount
  useEffect(() => {
    checkServerConnection();
    // Also validate the API key on mount
    validateApiKey();
  }, []);

  // Check server connection and update status
  const checkServerConnection = async () => {
    setServerStatus('Checking');
    try {
      // First check server health
      const healthResult = await checkServerHealth();
      
      if (healthResult.status === 'ok') {
        // Then test Google Sheets authentication
        const authResult = await testGoogleSheetsAuth();
        
        if (authResult.success) {
          setServerStatus('Connected');
        } else {
          setServerStatus('Disconnected');
          setError(`Google Sheets authentication failed: ${authResult.error || 'Unknown error'}`);
        }
      } else {
        setServerStatus('Disconnected');
        setError('Server health check failed');
      }
    } catch (err) {
      setServerStatus('Disconnected');
      setError('Failed to connect to the server');
      console.error('Connection check error:', err);
    }
  };

  // Validate API key
  const validateApiKey = async () => {
    setApiKeyValid(null); // Reset validation state
    
    try {
      // We'll use the existing implementation which handles API key in headers
      const healthResult = await checkServerHealth();
      
      if (healthResult.status === 'ok') {
        setApiKeyValid(true);
      } else {
        setApiKeyValid(false);
      }
    } catch (err) {
      setApiKeyValid(false);
      console.error('API key validation error:', err);
    }
  };

  // Handle form field changes
  const handleInputChange = (field: keyof GenerateAccessPassRequest, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for this field if any
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.customerName) {
      errors.customerName = 'Customer name is required';
    }
    
    if (!formData.contactNumber) {
      errors.contactNumber = 'Contact number is required';
    }
    
    if (!formData.validDate) {
      errors.validDate = 'Valid date is required';
    }
    
    if (!formData.pickupTime) {
      errors.pickupTime = 'Pickup time is required';
    }
    
    if (!formData.returnTime) {
      errors.returnTime = 'Return time is required';
    }
    
    if (!formData.type) {
      errors.type = 'Type is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setResponse(null);
    setError(null);
    
    try {
      const result = await generateAccessPass(formData);
      setResponse(result);
      
      if (!result.success) {
        setError(result.error || 'Failed to submit form');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Form submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Google Sheets Integration Test
          <Typography 
            component="span" 
            color={serverStatus === 'Connected' ? 'success.main' : 'error.main'}
            sx={{ ml: 2, fontSize: '1rem' }}
          >
            Server Status: {serverStatus === 'Checking' ? 'Checking...' : serverStatus}
          </Typography>
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            API Authentication
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                variant="outlined"
                error={apiKeyValid === false}
                helperText={apiKeyValid === false ? 'Invalid API key' : ''}
                InputProps={{
                  endAdornment: apiKeyValid === null ? null : (
                    <InputAdornment position="end">
                      {apiKeyValid ? (
                        <Typography color="success.main">Valid API key</Typography>
                      ) : (
                        <Typography color="error.main">Invalid API key</Typography>
                      )}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" component="h2" gutterBottom>
            Submit Form Data
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Name"
                value={formData.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                variant="outlined"
                error={!!formErrors.customerName}
                helperText={formErrors.customerName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number"
                value={formData.contactNumber}
                onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                variant="outlined"
                error={!!formErrors.contactNumber}
                helperText={formErrors.contactNumber}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Valid Date"
                type="date"
                value={formData.validDate}
                onChange={(e) => handleInputChange('validDate', e.target.value)}
                variant="outlined"
                error={!!formErrors.validDate}
                helperText={formErrors.validDate}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pickup Time"
                type="time"
                value={formData.pickupTime}
                onChange={(e) => handleInputChange('pickupTime', e.target.value)}
                variant="outlined"
                error={!!formErrors.pickupTime}
                helperText={formErrors.pickupTime}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Return Time"
                type="time"
                value={formData.returnTime}
                onChange={(e) => handleInputChange('returnTime', e.target.value)}
                variant="outlined"
                error={!!formErrors.returnTime}
                helperText={formErrors.returnTime}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.type} variant="outlined">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="Collection">Collection</MenuItem>
                  <MenuItem value="Return">Return</MenuItem>
                  <MenuItem value="Office Used">Office Used</MenuItem>
                  <MenuItem value="Delivery">Delivery</MenuItem>
                </Select>
                {formErrors.type && (
                  <Typography color="error" variant="caption">{formErrors.type}</Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={submitting || serverStatus !== 'Connected'}
                sx={{ py: 1.5 }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Submitting to Google Sheets
                  </>
                ) : (
                  'Submit to Google Sheets'
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {/* Response section */}
        {(response || error) && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Response
            </Typography>
            
            {error ? (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            ) : response && response.success ? (
              <Alert severity="success">
                <AlertTitle>Success</AlertTitle>
                Data successfully submitted to Google Sheets!
                {response.uniqueId && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Unique ID: {response.uniqueId}
                  </Typography>
                )}
              </Alert>
            ) : response ? (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {response.message || 'Failed to submit data'}
              </Alert>
            ) : null}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GoogleSheetsIntegrationTest; 